import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { RequestCancelAxios, SingletonRequest } from 'shared/api/lib'
import { type IInboxCombine, type IInboxData, type TYPES_INBOX } from 'entities/Inbox'
import { InboxesApi } from 'entities/Inbox/api/inboxes'
import { UsersApi } from 'entities/Users'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type {
  IParamsTeamsAll,
  IResponseEventInboxCalling,
  IResponseInboxCombine,
} from 'entities/Inbox/api/types'
import { UnifiedInbox } from 'entities/Inbox/model/UnifiedInbox'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { ChannelsInboxes } from 'entities/Inbox/channels/inboxes'
import { type Phone } from 'entities/Phone/model/Phone'
import type { EntitiesStore } from 'entities/EntitiesExport'
import { PhoneApi } from 'entities/Phone'
import { LocalInboxesStore } from 'entities/Inbox/store/LocalInboxesStore'
import { GroupInboxStore } from 'entities/Inbox/store/GroupInboxStore'

export class InboxesStore {
  initPromise
  private _setFinishInit: ((value?: unknown) => void) | null = null
  organizationInboxesInitPromise: Promise<unknown>
  private _setOrganizationFinishInit: ((value?: unknown) => void) | null = null
  private _loading = false
  private _inboxesLoadingIds: Set<number> = new Set()
  private _organizationInboxesLoading = false

  private _inboxesMap: Map<number, IInboxCombine> = new Map()
  private _currentInboxId: number | null = null
  private _currentInboxType: TYPES_INBOX | null = null
  private _currentInboxTypeId: number | null = null

  readonly channelsInboxes: ChannelsInboxes
  readonly localInboxes: LocalInboxesStore
  readonly groupInboxStore: GroupInboxStore

  private _disposeCurrentInboxId: ReturnType<typeof reaction> | null = null
  private readonly _requestCancelAxios = new RequestCancelAxios()

  constructor(private _entities: EntitiesStore) {
    this.localInboxes = new LocalInboxesStore(this._entities)
    this.groupInboxStore = new GroupInboxStore(this)
    this.channelsInboxes = new ChannelsInboxes(this, this._entities.websocket)

    this.reactionCurrentInboxId()

    this.initPromise = new Promise((resolve) => {
      this._setFinishInit = resolve
    })
    this.organizationInboxesInitPromise = new Promise((resolve) => {
      this._setOrganizationFinishInit = resolve
    })
    makeAutoObservable(this)
  }

  private _fetchInboxes = async () => {
    if (this.personalInboxes.length) {
      this._setFinishInit?.()
      return
    }

    try {
      runInAction(() => {
        this._loading = true
      })

      await Promise.all([this.fetchUser(), this.fetchPersonalInboxes(), this.fetchGroupInboxes()])

      this.channelsInboxes.subscribeChannels()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
      this._setFinishInit?.()
    }
  }
  fetchInboxes = new SingletonRequest(this._fetchInboxes).request

  private _fetchUser = async () => {
    try {
      const userResponse = await this._entities.usersStore.fetchUsersMe()

      if (userResponse) {
        this.getById(userResponse.currentInboxId, true)
      }

      runInAction(() => {
        if (userResponse) {
          this._entities.usersStore.addItem(userResponse)
          this.initInboxCurrent(userResponse)
        }
      })
    } catch (e) {
      logger.error(e)
    }
  }

  fetchUser = new SingletonRequest(this._fetchUser).request

  private _fetchPersonalInboxes = async () => {
    try {
      const { data } = await InboxesApi.getInboxesPersonal({
        withUnified: true,
      })

      this.addItems(data)
    } catch (e) {
      logger.error(e)
    }
  }

  fetchPersonalInboxes = new SingletonRequest(this._fetchPersonalInboxes).request

  private _fetchGroupInboxes = async () => {
    try {
      const { data } = await InboxesApi.getGroupInboxes()

      this.addItems(data)
    } catch (e) {
      logger.error(e)
    }
  }

  fetchGroupInboxes = new SingletonRequest(this._fetchGroupInboxes).request

  private _loadOrganizationInboxes = async () => {
    try {
      runInAction(() => {
        this._organizationInboxesLoading = true
      })

      const { data } = await this.getTeamsAll()

      runInAction(() => {
        data.forEach((item) => {
          this._entities.numbersStore.addItems(item.numbers)

          const existing = this._inboxesMap.get(item.id)

          if (existing instanceof Inbox) {
            existing.syncOrigin({ ...existing, ...item })
          } else {
            this._inboxesMap.set(item.id, new Inbox(item, this._entities))
          }
        })
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._organizationInboxesLoading = false
      })
      this._setOrganizationFinishInit?.()
    }
  }

  loadOrganizationInboxes = new SingletonRequest(this._loadOrganizationInboxes).request

  getTeamsAll = new SingletonRequest((params?: IParamsTeamsAll) => InboxesApi.getTeamsAll(params))
    .request

  initInboxCurrent = (data: IInboxData) => {
    this._currentInboxId = data.currentInboxId
    this._currentInboxType = data.currentInboxType
    this._currentInboxTypeId = data.currentInboxTypeId

    if (this._currentInboxId) {
      this.localInboxes.resetCurrentInbox()
    } else {
      this.localInboxes.initInboxQuickFilter()
    }
  }

  addItems = (items: IResponseInboxCombine[]) => {
    items.forEach((item) => {
      if (item.type === 'inbox') {
        this._entities.numbersStore.addItems(item.numbers)
      }

      this.addItem(item)
    })

    return items.reduce<IInboxCombine[]>((state, item) => {
      const inbox = this.getItem(item.id)

      if (inbox) state.push(inbox)

      return state
    }, [])
  }

  deleteItem = (id: number) => {
    this._inboxesMap.delete(id)
  }

  removeMemberFromInbox = (inboxId: number, memberId: number) => {
    const inbox = this._inboxesMap.get(inboxId)

    if (inbox instanceof Inbox) {
      inbox.removeMemberId(memberId)
    }
  }

  getItem = (id?: number | null) => {
    if (typeof id !== 'number') return

    return this._inboxesMap.get(id)
  }

  getItemShared = (id?: number) => {
    if (!id) return

    const inbox = this._inboxesMap.get(id)

    if (inbox?.type !== 'inbox') return

    return inbox
  }

  createInboxItem = (data: IResponseInboxCombine) => {
    if (data.type === 'unified-inbox') {
      return new UnifiedInbox(data, this._entities)
    }
    if (data.type === 'inbox_group') {
      return new GroupInbox(data, this._entities)
    }
    return new Inbox(data, this._entities)
  }

  addItem = (data: IResponseInboxCombine) => {
    const existing = this._inboxesMap.get(data.id)

    if (existing) {
      if (existing instanceof Inbox && data.type === 'inbox') {
        existing.syncOrigin({ ...existing, ...data })
      }
      if (existing instanceof GroupInbox && data.type === 'inbox_group') {
        existing.syncOrigin({ ...existing, ...data })
      }
      if (existing instanceof UnifiedInbox && data.type === 'unified-inbox') {
        existing.syncOrigin({ ...existing, ...data })
      }
    } else {
      const created = this.createInboxItem(data)
      this._inboxesMap.set(data.id, created)

      if (created instanceof Inbox && data.type === 'inbox') {
        this._entities.numbersStore.addItems(data.numbers)
      }
    }

    return this._inboxesMap.get(data.id)
  }

  handleUpdateTeamInbox = async (inboxId: number, type: 'default' | 'fast' = 'default') => {
    if (this._currentInboxId === inboxId) return

    try {
      const inbox = await this.getById(inboxId)

      if (inbox) {
        this._requestCancelAxios.init()

        if (inbox.id) {
          this.localInboxes.resetCurrentInbox()
        }

        if (type === 'fast') {
          runInAction(() => {
            this._currentInboxId = inbox.id
            this._currentInboxType = inbox.inboxType
            this._currentInboxTypeId = inbox.id
          })
        }

        await UsersApi.updateUsersTeam(inbox.bodyUpdateUserTeam, {
          cancelToken: this._requestCancelAxios.token,
        })

        if (type === 'default') {
          runInAction(() => {
            this._currentInboxId = inbox.id
            this._currentInboxType = inbox.inboxType
            this._currentInboxTypeId = inbox.id
          })
        }
      }
    } catch (e) {
      logger.error(e)
    }
  }

  updateFavorite = async (inbox: IInboxCombine, status: boolean) => {
    if (inbox.is_favorite === status) return

    await InboxesApi.updateTeamsFavorite(inbox.bodyUpdateTeamsFavorite)

    runInAction(() => {
      inbox.is_favorite = status
    })
  }

  handleUpdateTeamsMute = async (inbox: IInboxCombine, status: boolean) => {
    if (inbox.type !== 'inbox') return
    if (inbox.isMuted === status) return

    await InboxesApi.updateTeamsMute(inbox.id, {
      is_muted: inbox.isMuted ? 0 : 1,
    })

    runInAction(() => {
      inbox.handleUpdateIsMuted(status)
    })
  }

  getById = async (id: number, update?: boolean, withError?: boolean) => {
    try {
      let team = this.getItem(id)

      if (id && !team && !this._inboxesLoadingIds.has(id)) {
        this._inboxesLoadingIds.add(id)
        const { data } = await InboxesApi.getTeamById(id)

        team = new Inbox(data, this._entities)

        if (update) {
          this.addItem(data)
        }
      }

      return team
    } catch (e) {
      logger.error(e)
      if (withError) {
        throw e
      }
    } finally {
      runInAction(() => {
        this._inboxesLoadingIds.delete(id)
      })
    }
  }

  fetchInboxesByIds = async (ids: number[]): Promise<IInboxCombine[]> => {
    const inboxes = await Promise.all(
      ids.map(async (id) => {
        try {
          return await this.getById(id, true)
        } catch (error) {
          console.error(`Failed to fetch inbox ${id}:`, error)
          return null
        }
      })
    )
    return inboxes.filter((inbox): inbox is IInboxCombine => inbox !== null && inbox !== undefined)
  }

  getInboxByNumber = (numberId: number) => {
    return this.sharedInboxes.find((inbox) => inbox.numberIds.includes(numberId))
  }

  getInboxByNumberAsync = async (numberId: number) => {
    try {
      const inbox = this.getInboxByNumber(numberId)

      if (!inbox) {
        const response = await PhoneApi.getTeamByNumberId(numberId)

        return this.addItem(response.data.data)
      }

      return inbox
    } catch (e) {
      logger.error(e)
    }
  }

  updateInboxCalling = (data: IResponseEventInboxCalling) => {
    const inbox = this.getItem(data.team_id)

    if (inbox?.type === 'inbox') {
      const number = this._entities.numbersStore.getItem(inbox?.numberId)

      if (number) {
        number.updateInboxCalling(data.number)
      }
    }
  }

  reactionCurrentInboxId = () => {
    this._disposeCurrentInboxId?.()
    this._disposeCurrentInboxId = reaction(
      () => this._currentInboxId,
      async (currentInboxId) => {
        if (!currentInboxId) return

        let inbox = this.getItem(currentInboxId)

        if (inbox instanceof GroupInbox) {
          inbox = this.getItem(inbox.teamsIds[0])
        }

        if (!inbox) return

        try {
          const { data } = await InboxesApi.getTeamByIdMembers(inbox.id)
          this._entities.usersStore.addItems(data)
        } catch (e) {
          console.error('ERROR getTeamByIdMembers: ', e)
        }
      }
    )
  }

  get isUnifiedInboxOnly() {
    const accessibleInboxes = this.personalInboxes.filter((inbox) => {
      if (inbox instanceof GroupInbox) {
        return inbox.teamsIds.some((id) => !!this.getItem(id))
      }

      return true
    })

    return accessibleInboxes.length === 1 && accessibleInboxes[0].type === 'unified-inbox'
  }

  get personalInboxes() {
    const currentUserId = this._entities.usersStore.user?.id

    return Array.from(this._inboxesMap.values()).filter((inbox) => {
      if (!(inbox instanceof Inbox)) return true
      if (!currentUserId) return false

      return inbox.memberIds.includes(currentUserId)
    })
  }

  get hasRcsInbox() {
    return !!this.rcsInbox
  }

  get rcsInbox(): Inbox | null {
    const personalRcs = this.sharedInboxes.find((inbox) => inbox.isRCS)

    if (personalRcs) return personalRcs

    if (this._entities.usersStore.user?.isAdminOrOwnerRole) {
      return this._entities.rcsStore.rcsOrganizationInbox ?? null
    }

    return null
  }

  get sharedInboxes() {
    return this.personalInboxes.reduce<Inbox[]>((state, inbox) => {
      if (inbox.type === 'inbox') {
        state.push(inbox)
      }

      return state
    }, [])
  }

  get groupsInboxes() {
    return this.personalInboxes.reduce<GroupInbox[]>((state, inbox) => {
      if (inbox.type === 'inbox_group') {
        state.push(inbox)
      }

      return state
    }, [])
  }

  get unifiedInboxes() {
    return this.personalInboxes.reduce<UnifiedInbox[]>((state, inbox) => {
      if (inbox.type === 'unified-inbox') {
        state.push(inbox)
      }

      return state
    }, [])
  }

  get hasInboxWithCurrentUserAsOwner() {
    const currentUserId = this._entities.usersStore.user?.id

    return this.sharedInboxes.some((inbox) => inbox.ownerId === currentUserId)
  }

  get nonPFTInboxNumberType() {
    const inbox = this.personalInboxes.find((inbox) => !inbox.isPFTInbox && inbox.type === 'inbox')

    if (!(inbox instanceof Inbox)) return undefined

    return inbox.number?.type
  }

  private get _firstInboxId() {
    const inboxIds = this.personalInboxes.reduce<number[]>((acc, data) => {
      if (data?.type === 'unified-inbox') return acc

      if (data?.type === 'inbox_group') {
        if (data.teamsIds.length) {
          const sortedTeams = data.origin.teams.sort((a, b) => a.name.localeCompare(b.name))
          const teamInbox = this.getItem(sortedTeams[0].id)

          if (!teamInbox) return acc
          if (teamInbox.isFavoriteType) return [teamInbox.id, ...acc]
          if (!teamInbox.isFavoriteType) return [...acc, teamInbox.id]

          return acc
        }
      }

      if (data?.type !== 'inbox') return acc
      if (data.isFavoriteType) return [data.id, ...acc]
      if (!data.isFavoriteType) return [...acc, data.id]

      return acc
    }, [])

    return inboxIds[0]
  }

  get sortedFirstInbox() {
    return this.getItem(this._firstInboxId)
  }

  getNumbersByTeamIds = (teamIds: number[]) => {
    let numbers: Phone[] = []
    const inboxes = teamIds.map((id) => this.getItem(id))
    inboxes.forEach((inbox) => {
      if (inbox instanceof Inbox) {
        numbers = numbers.concat(inbox.numbers)
      }
    })
    return numbers
  }

  get unreadMessagesCount() {
    return this.personalInboxes
      .map((item) => item.unreadCount)
      .filter((item) => item)
      .reduce((state, value) => state + value, 0)
  }

  get currentInbox() {
    if (this._currentInboxId === null) return

    return this.getItem(this._currentInboxId)
  }

  get isUnifiedInbox() {
    return this._currentInboxId === 0
  }

  get currentInboxId() {
    return this._currentInboxId
  }

  get currentInboxTypeId() {
    return this._currentInboxTypeId
  }

  get currentInboxType() {
    return this._currentInboxType
  }

  get loading() {
    return this._loading
  }

  get organizationInboxes(): Inbox[] {
    return Array.from(this._inboxesMap.values())
      .reduce<Inbox[]>((state, inbox) => {
        if (inbox instanceof Inbox) state.push(inbox)

        return state
      }, [])
      .sort((a, b) => {
        const timeA = new Date(a.updatedAt).getTime()
        const timeB = new Date(b.updatedAt).getTime()

        if (isNaN(timeA) || isNaN(timeB)) return 0

        return timeB - timeA
      })
  }

  get organizationInboxesLoading() {
    return this._organizationInboxesLoading
  }
}
